import { useRef, useState, useMemo, useEffect, useCallback, memo } from 'react';
import { debounce } from 'lodash';
import { useNTContext } from './NTContext';
import { useNTUtilsContext } from './NTUtilsContext';
import { useNTTableContext } from './NTTableContext';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useReportsStore } from 'hooks/useReportsStore';
import { NewTableProps } from './types';

import { useColumnWidths } from './hooks/useColumnWidths';
import parseReportNT from '../KSExport/reportNTParser';
import { saveURLParams } from './utils/urlParams';
import { NTMainLayout } from './NTMainLayout';
import { useNTScroll } from './NTScrollContext';
import { useNTSelectionContext } from './NTSelectionContext';
import { getReportCapabilities } from '@hone-automation/common';
import NTAttachmentManager from './NTAttachments';
import { useQueueRequestsQuery } from 'hooks/useQueueRequestsQuery';
import { HoneReportTypes } from '../../../constants';
import { useQueryState } from 'hooks/useQueryState';
import { useGetReportConfiguration } from 'hooks';
import { ReportConfiguration, ReportTimeframe } from '@hone-automation/common';
import { isBudgetRangeOverlapping } from '../ReportPLDatesModal/utils';
import { useTemplatesRangesQuery } from 'hooks/reports/useTemplatesRangesQuery';

const MemoizedNewTableContent = memo(
  ({ report, reportId, fixFirstColumn = false, loading = false, reportType, urlParams }: NewTableProps) => {
    // Context hooks first
    const [reverse] = useQueryState<string>('reverse');
    const [consolidated] = useQueryState<string>('consolidated');
    const [audit] = useQueryState<string>('audit');
    const { filters, setFilters, budgetAvailable = false, setBudgetAvailable, setReportId } = useNTContext();
    const { actions, setActions, capabilities, setCapabilities, accountVisualFilters, setAccountVisualFilters } =
      useNTUtilsContext();

    const [urlTParams, setUrlTParams] = useState<URLSearchParams>(new URLSearchParams(location.search));

    // Use a ref to track whether we've initialized from URL params
    const initialUrlParamsRef = useRef<boolean>(false);
    const { transformedData, setReportViewModel, cellStates, setCellStates, setSelectionHistory } =
      useNTSelectionContext();
    const { setTableWidth } = useNTTableContext();

    // Calculate column widths with stable dependencies
    useColumnWidths(transformedData, filters, capabilities, budgetAvailable || false, audit);

    // Refs
    const parentRef = useRef<HTMLDivElement>(null);
    const trace = false;

    // State hooks
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Data fetching hooks
    const currentLocation = useLocationsStore(state => state.currentLocation);
    const { data: reportsConfiguration } = useGetReportConfiguration(currentLocation?.id) as {
      data: ReportConfiguration | undefined;
    };
    const { data: flaggedTransactions } = useQueueRequestsQuery();
    const { data: availableTemplates } = useTemplatesRangesQuery();

    // Memoized callbacks
    const handleSearch = useCallback((value: string) => {
      setSearchTerm(value);
    }, []);
    const handleClear = useCallback(() => setSearchTerm(''), []);

    // States
    const [envReady, setEnvReady] = useState(true);
    const [ready, setReady] = useState(false);

    const mustCleanData = useRef(true);

    useEffect(() => {
      setReady(false);
      setEnvReady(false);
      setReportViewModel(null);
    }, []);

    // First Step - Fetching data
    useEffect(() => {
      if (loading) {
        if (ready) setReady(false);
        setEnvReady(false);
        setReportViewModel(null);
      }
    }, [loading]);

    // Second Step - Parsing data
    // Don't set ready to false here - let the reverse effect handle it
    useEffect(() => {
      if (!report) return;
      // Removed: if (ready) setReady(false);
      // This was causing double calculations
    }, [report?.createdAt, report?.type, report?.dates, report?.title]);

    // Updating the reportId if we get it
    useEffect(() => {
      if (report && reportId) {
        setReportId(reportId);
      }
    }, [report, reportId]);

    // Backend reverse is handled via URL parameter - no state synchronization needed
    const [lastDatesKey, setLastDatesKey] = useState<string>('');
    const [lastReverseState, setLastReverseState] = useState<string | undefined>(reverse);

    const { setEnableAIReport } = useReportsStore();
    // Get LaunchDarkly flags from context instead of direct subscription
    // This prevents an additional LaunchDarkly subscription
    const { featureFlags: launchDarklyFlags } = useNTUtilsContext();

    // Create a stable key from dates to detect changes
    const datesKey = useMemo(() => {
      return report?.dates?.map(d => d.start).join(',') || '';
    }, [report?.dates]);

    // Instead of directly calling setReportViewModel with useMemo
    const transformedDataValue = useMemo(() => {
      if (!report || !envReady) {
        return null;
      }

      // Check if reverse state changed but we still have old data
      // If reverse changed AND datesKey is the same as last time, skip calculation
      if (reverse !== lastReverseState && datesKey === lastDatesKey && lastDatesKey !== '') {
        return null;
      }

      return parseReportNT({
        report,
        reportId,
        useComputedColumns: true,
        periodReporting: reportsConfiguration?.data?.periodReporting,
        reportCFG: reportsConfiguration,
        flaggedTransactions,
        currentLocation,
        budget: reportType === 'budget',
        trace,
        consolidated: consolidated === 'true',
        reversedReport: false, // Backend handles reversal - always false
        extendedAnalysis: true,
        requestedTimeframe: (urlTParams?.get('timeframe') ?? undefined) as ReportTimeframe,
        launchDarklyFlags, // Pass LaunchDarkly flags for feature flag control
      });
    }, [
      datesKey, // Primary dependency - detects when dates order changes
      report, // Need report object to access sections data
      reportId,
      currentLocation,
      reportsConfiguration,
      flaggedTransactions,
      reportType,
      consolidated,
      envReady,
      reverse, // Include reverse to trigger re-evaluation
      lastReverseState, // Track last reverse state
      lastDatesKey, // Track last dates key
      launchDarklyFlags, // Include flags to trigger re-evaluation if V2 flag changes
    ]);

    // Clear transformed data when reverse changes but data hasn't updated yet
    useEffect(() => {
      if (reverse !== lastReverseState && datesKey === lastDatesKey && lastDatesKey !== '') {
        setReportViewModel(null);
        setReady(false);
      }
    }, [reverse, lastReverseState, datesKey, lastDatesKey]);

    // Update AI report flag when transformed data changes
    // V2 schema: check analysis.aiInsights instead of enableAIReport
    // Use != null to handle both null and undefined (matches getEnableAIReport semantics)
    useEffect(() => {
      setEnableAIReport(transformedDataValue?.analysis?.aiInsights != null);
    }, [transformedDataValue?.analysis?.aiInsights]);

    // Create a stable AI data object that doesn't change with every transformedData update
    // V2 schema: structure must match what getAIAnalysis() expects (data.analysis.aiInsights)
    const aiData = useMemo(() => {
      if (!transformedDataValue) return null;
      return {
        reportId: transformedDataValue.reportId,
        analysis: {
          aiInsights: transformedDataValue.analysis?.aiInsights,
        },
        type: transformedDataValue.type,
      };
    }, [transformedDataValue?.reportId, transformedDataValue?.analysis?.aiInsights]);

    // Use useEffect to set the transformed data
    // Set Ready to true when the transformed data is set
    useEffect(() => {
      if (!transformedDataValue || !envReady) return;
      setReportViewModel(transformedDataValue);
      setLastDatesKey(datesKey); // Track the dates we just processed
      setLastReverseState(reverse); // Track the reverse state we just processed
      setSelectionHistory([]);
      setReady(true);
    }, [transformedDataValue, setReportViewModel, envReady, datesKey, reverse]);

    // Setting the default rowId (selected) if we don't get one
    useEffect(() => {
      if (!transformedData || !transformedData.columns || !envReady) return;
      if (!cellStates.selected && transformedData.columns[0]?.data) {
        setCellStates({ ...cellStates, selected: transformedData.columns[0].data[1]?.id });
      }
    }, [transformedData, cellStates.selected, envReady]);

    // Initialize from urlParams passed down from NewTable
    useEffect(() => {
      // TO-DO move all this logic to the parent component, since the Context covers the parent too
      // Only run this once on initial load
      if (initialUrlParamsRef.current || !urlParams) return;
      if (!report || !report.type) return;

      const { filter, urlActions, selection, urlBudgetInclude, accountVFilters } = urlParams;

      // Mark that we've processed the URL parameters
      initialUrlParamsRef.current = true;

      const initCapabilities = getReportCapabilities(reportType === 'budget' ? 'Budget' : report!.type);
      const dateTRange = urlTParams.get('dateRange');
      const templateId = urlTParams.get('templateId');
      const currentTemplate = availableTemplates?.find(t => t.id === templateId);
      //validate if have date range to compare and check if budget are avaibale in that range, if not use the default report range to check
      const startDate = dateTRange ? dateTRange?.split(',')[0] : report?.dates?.[0]?.start;
      const endDate = dateTRange ? dateTRange?.split(',')[1] : report?.dates?.[0]?.end;
      const hasBudget = isBudgetRangeOverlapping(startDate, endDate, currentTemplate?.budgetRanges);
      // Apply all settings at once to avoid race conditions
      setCapabilities(initCapabilities);
      setBudgetAvailable(hasBudget && urlBudgetInclude);
      setFilters({ ...filters, ...filter });
      setAccountVisualFilters(accountVFilters);
      setActions({
        ...actions,
        total: urlActions.total,
        difference: urlActions.difference,
        detailsPanel: urlActions.detailsPanel,
      });

      setCellStates({
        ...cellStates,
        selected: selection.rowID,
        selectedColumn: selection.columnID,
      });

      const mustOpenDetailsPanel = urlActions.detailsPanel && initCapabilities.charts;
      const tableWidthFromURL = new URLSearchParams(window.location.search).get('tableWidth');
      const hasValidTableWidth =
        typeof tableWidthFromURL === 'string' &&
        (/^\d+(\.\d+)?px$/.test(tableWidthFromURL) || /^\d+(\.\d+)?%$/.test(tableWidthFromURL));
      setTableWidth(mustOpenDetailsPanel ? (hasValidTableWidth ? tableWidthFromURL : '68%') : '100%');
      setEnvReady(true);
    }, [report, report?.type, reportType, urlParams]);

    // Debounced function to save URL params
    const debouncedSaveURLParams = useCallback(
      debounce(params => {
        saveURLParams(params);
      }, 300),
      []
    );

    // Sync URL params when filters or actions change
    useEffect(() => {
      if (!envReady || report?.type !== HoneReportTypes.PLComparison) return;
      // We still need to save URL params when state changes
      debouncedSaveURLParams({
        filters,
        actions,
        cellState: cellStates,
        accountVisualFilters,
        budgetAvailable,
      });

      // Cleanup function to cancel any pending debounced calls
      return () => {
        debouncedSaveURLParams.cancel();
      };
    }, [filters, actions, cellStates, accountVisualFilters, envReady, debouncedSaveURLParams, budgetAvailable]);

    // Memoize the NTMainLayout component. It will be drawn when is ready
    const MemoizedNTMainLayout = useMemo(() => {
      if (!envReady) return null;
      return (
        <>
          <NTMainLayout
            parentRef={parentRef}
            transformedData={transformedData}
            aiData={aiData}
            loading={!ready}
            searchTerm={searchTerm}
            fixFirstColumn={fixFirstColumn}
            onSearch={handleSearch}
            onClear={handleClear}
          />
          <NTAttachmentManager />
        </>
      );
    }, [ready, envReady, report?.type, searchTerm, transformedData, aiData, actions]);

    // Early return with stable condition
    if (!report || !transformedData || !transformedData.columns.length) {
      if (!report) {
        if (!mustCleanData.current) return;
        mustCleanData.current = false;
        return null;
      }
    }

    // Then in the return statement:
    return <>{MemoizedNTMainLayout}</>;
  },
  (prevProps, nextProps) => {
    // Only re-render if these props change
    return (
      prevProps.report === nextProps.report &&
      prevProps.report?.createdAt === nextProps.report?.createdAt &&
      prevProps.loading === nextProps.loading &&
      prevProps.fixFirstColumn === nextProps.fixFirstColumn &&
      prevProps.fixLastColumn === nextProps.fixLastColumn &&
      prevProps.redraw === nextProps.redraw
    );
  }
);

export const NewTableContent: React.FC<NewTableProps> = props => {
  // Get scroll context here but don't pass it down
  const { scrollState, scrollChange } = useNTScroll();
  // TODO: Initialize the ScrollState here
  return <MemoizedNewTableContent {...props} />;
};
